import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { PositionAndDimensionsType, PositionType } from 'data/types';
import { polygonBoundingBox } from './footprint';
import {
  footprintAreaLabelBox,
  formatFootprintAreaLabel,
  getFootprintDimensionsLabel,
  measureFootprintAreaLabel
} from './footprint-area-label-layout';
import { FootprintAreaLabelMetrics } from './footprint-area-label-layout-types';
import { FootprintAreaLabelPlacement } from './footprint-area-label-placement-types';
import { calculateFootprintArea } from './footprint-scaling';
import { isValidFootprintShape } from './footprint-union';
import { polygonsOverlap, rectToAbsolutePolygon } from './space-polygon';

const LABEL_GAP_PX = 4;
const LABEL_PADDING_PX = 2;

type Axis = 'x' | 'y';

function toPixels(polygon: PositionType[]): PositionType[] {
  return polygon.map((point) => ({
    x: point.x * STRUCTURE_GRID_SIZE,
    y: point.y * STRUCTURE_GRID_SIZE
  }));
}

function edgeRunCenter(
  polygon: PositionType[],
  axis: Axis,
  value: number
): number {
  const other: Axis = axis === 'x' ? 'y' : 'x';
  let bestLength = -1;
  let bestCenter = 0;
  polygon.forEach((start, index) => {
    const end = polygon[(index + 1) % polygon.length];
    if (start[axis] !== value || end[axis] !== value) return;
    const length = Math.abs(end[other] - start[other]);
    if (length <= bestLength) return;
    bestLength = length;
    bestCenter = (start[other] + end[other]) / 2;
  });
  if (bestLength >= 0) return bestCenter;
  const onLine = polygon.filter((point) => point[axis] === value);
  return onLine.reduce((sum, point) => sum + point[other], 0) / onLine.length;
}

function candidateAnchors(
  polygon: PositionType[],
  metrics: FootprintAreaLabelMetrics
): PositionType[] {
  const { minX, minY, maxX, maxY } = polygonBoundingBox(polygon);
  const centerX = (minX + maxX) / 2;
  const topRunX = edgeRunCenter(polygon, 'y', minY);
  const bottomRunX = edgeRunCenter(polygon, 'y', maxY);
  const halfWidth = metrics.width / 2;
  const belowY = maxY + LABEL_GAP_PX - metrics.top;
  const sideY = (runY: number) => runY - (metrics.top + metrics.bottom) / 2;
  return [
    { x: topRunX, y: minY },
    { x: centerX, y: minY },
    { x: bottomRunX, y: belowY },
    { x: centerX, y: belowY },
    {
      x: minX - LABEL_GAP_PX - halfWidth,
      y: sideY(edgeRunCenter(polygon, 'x', minX))
    },
    {
      x: maxX + LABEL_GAP_PX + halfWidth,
      y: sideY(edgeRunCenter(polygon, 'x', maxX))
    },
    { x: topRunX, y: minY + LABEL_GAP_PX - metrics.top }
  ];
}

function padBox(
  box: PositionAndDimensionsType,
  padding: number
): PositionAndDimensionsType {
  return {
    x: box.x - padding,
    y: box.y - padding,
    width: box.width + padding * 2,
    height: box.height + padding * 2
  };
}

function rectsOverlap(
  a: PositionAndDimensionsType,
  b: PositionAndDimensionsType
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function countConflicts(
  box: PositionAndDimensionsType,
  obstacles: PositionType[][],
  placedBoxes: PositionAndDimensionsType[]
): number {
  const padded = padBox(box, LABEL_PADDING_PX);
  const boxPolygon = rectToAbsolutePolygon(padded);
  const polygonHits = obstacles.filter((polygon) =>
    polygonsOverlap(boxPolygon, polygon)
  ).length;
  const labelHits = placedBoxes.filter((other) =>
    rectsOverlap(padded, other)
  ).length;
  return polygonHits + labelHits;
}

function chooseAnchor(
  polygon: PositionType[],
  metrics: FootprintAreaLabelMetrics,
  obstacles: PositionType[][],
  placedBoxes: PositionAndDimensionsType[]
): PositionType {
  const candidates = candidateAnchors(polygon, metrics);
  let best = candidates[0];
  let bestConflicts = Infinity;
  for (const candidate of candidates) {
    const conflicts = countConflicts(
      footprintAreaLabelBox(candidate, metrics),
      obstacles,
      placedBoxes
    );
    if (conflicts === 0) return candidate;
    if (conflicts < bestConflicts) {
      best = candidate;
      bestConflicts = conflicts;
    }
  }
  return best;
}

function labelsFor(
  polygon: PositionType[]
): Pick<FootprintAreaLabelPlacement, 'areaLabel' | 'dimensionsLabel'> | null {
  const area = calculateFootprintArea(polygon);
  if (area <= 0) return null;
  return {
    areaLabel: formatFootprintAreaLabel(area),
    dimensionsLabel: getFootprintDimensionsLabel(polygon)
  };
}

export function placeFootprintAreaLabels(
  polygons: PositionType[][]
): (FootprintAreaLabelPlacement | null)[] {
  const pixelPolygons = polygons.map((polygon) =>
    isValidFootprintShape(polygon) ? toPixels(polygon) : null
  );
  const placedBoxes: PositionAndDimensionsType[] = [];
  return pixelPolygons.map((polygon, index) => {
    const labels = polygon ? labelsFor(polygons[index]) : null;
    if (!polygon || !labels) return null;
    const metrics = measureFootprintAreaLabel(
      labels.areaLabel,
      labels.dimensionsLabel
    );
    const obstacles = pixelPolygons.filter(
      (other, otherIndex): other is PositionType[] =>
        other !== null && otherIndex !== index
    );
    const anchor = chooseAnchor(polygon, metrics, obstacles, placedBoxes);
    placedBoxes.push(footprintAreaLabelBox(anchor, metrics));
    return { anchor, ...labels };
  });
}
